import type { AuthConfig } from '@/domains/appShell/stores/auth.store'
import { isLocalEnvironment } from '@/isLocalEnvironment'
import type { ApiConfig } from '@/shared/types/api'

import type { InitOpts } from '@hotjar/browser/dist/utils'
import type { IConfiguration } from '@microsoft/applicationinsights-web'
import * as Sentry from '@sentry/react'

export type Config = {
  appInsightKey: string
  env: string
  auth: AuthConfig
  api: ApiConfig
  delayIdleTimer: number
  /** Enable testing tooling (ErrorSimulator, FeatureToggles). Only available in local and dev environments. */
  testingTooling: boolean
  sentry: Sentry.BrowserOptions
  hotjar?: {
    hotjarId: number
    hotjarVersion: number
    opts?: InitOpts
  }
  applicationInsights?: IConfiguration
  workflowUrl: string
  powerBIUrl: string
  crmUrl: string
}

export const getEnvironmentConfig = (hostname?: string) => {
  const actualHostname =
    hostname ?? (typeof window !== 'undefined' ? window.location.hostname : 'localhost')

  // TODO: Remove the temporary Azure Static Web Apps hostname after the production cutover.
  if (
    actualHostname === 'trustview-backoffice.ardian.com' ||
    actualHostname === 'trustview-backoffice-alpha.ardian.com'
  ) {
    const appInsightKey = 'c1d655e9-9389-4365-bc68-9a98f9b73d9b'

    return {
      testingTooling: false,
      appInsightKey,
      env: 'P',
      auth: {
        entra: {
          clientId: 'af5dc7f6-a00e-4667-8b13-44097982184f',
          authority: 'https://login.microsoftonline.com/19843fff-dfd9-47f6-ac0c-6dafee40bd86',
          scopes: ['api://af5dc7f6-a00e-4667-8b13-44097982184f/bo'],
        },
      },
      api: {
        baseURL: 'https://apeazapi11p.azure-api.net/trustview/bo/v1/',
        timeout: 40000,
      },
      hotjar: {
        hotjarId: 5253990,
        hotjarVersion: 6,
      },
      applicationInsights: {
        connectionString: `InstrumentationKey=${appInsightKey}`,
        enableAutoRouteTracking: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxErrorStatusText: true,
        enableAjaxPerfTracking: true,
        enableUnhandledPromiseRejectionTracking: true,
      },
      workflowUrl: 'https://wkf.ardian.com/communicationtolps',
      powerBIUrl: 'http-[r][r]s://app.powerbi.com',
      crmUrl: 'https://ri-prod.crm12.dynamics.com',
    }
  }

  // TODO: Remove the temporary Azure Static Web Apps hostname after the UAT cutover.
  if (
    actualHostname === 'trustview-backoffice-uat.ardian.com' ||
    actualHostname === 'trustview-backoffice-alpha-uat.ardian.com'
  ) {
    const appInsightKey = 'e9062aa3-63e2-4347-9125-fce5a2db8bfe'

    return {
      testingTooling: false,
      appInsightKey,
      env: 'U',
      auth: {
        entra: {
          clientId: '8d6be7a1-b1d4-4d92-ac93-4f47bb5a8563',
          authority: 'https://login.microsoftonline.com/19843fff-dfd9-47f6-ac0c-6dafee40bd86',
          scopes: ['api://8d6be7a1-b1d4-4d92-ac93-4f47bb5a8563/bo'],
        },
      },
      api: {
        baseURL: 'https://apeazapi11u.azure-api.net/trustview/bo/v1/',
        timeout: 40000,
      },
      hotjar: {
        hotjarId: 5253990,
        hotjarVersion: 6,
      },
      applicationInsights: {
        connectionString: `InstrumentationKey=${appInsightKey}`,
        enableAutoRouteTracking: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxErrorStatusText: true,
        enableAjaxPerfTracking: true,
        enableUnhandledPromiseRejectionTracking: true,
      },
      workflowUrl: 'https://wkf-uat.ardian.com/communicationtolps',
      powerBIUrl: 'https://app.powerbi.com',
      crmUrl: 'https://ri-uat.crm12.dynamics.com',
    }
  }

  const appInsightKey = '6ef4a639-3d8c-4b86-9cee-74a83699842f'

  return {
    testingTooling: true,
    appInsightKey,
    env: 'D',
    auth: {
      entra: {
        clientId: '37f128ea-71f6-41bf-b971-621979171c35',
        authority: 'https://login.microsoftonline.com/19843fff-dfd9-47f6-ac0c-6dafee40bd86',
        scopes: ['api://37f128ea-71f6-41bf-b971-621979171c35/bo'],
      },
    },
    api: {
      baseURL: isLocalEnvironment()
        ? 'http://localhost:8000/bo/v1/'
        : 'https://apeazapi11d.azure-api.net/trustview/bo/v1/',
      timeout: 40000,
    },
    hotjar: {
      hotjarId: 5253990,
      hotjarVersion: 6,
    },
    applicationInsights: {
      connectionString: `InstrumentationKey=${appInsightKey}`,
      enableAutoRouteTracking: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      enableAjaxErrorStatusText: true,
      enableAjaxPerfTracking: true,
      enableUnhandledPromiseRejectionTracking: true,
    },
    workflowUrl: 'https://wkf-dev.ardian.com/communicationtolps',
    powerBIUrl: 'https://app.powerbi.com',
    crmUrl: 'https://ri-dev.crm12.dynamics.com',
  }
}

const environmentConfig = getEnvironmentConfig()

export const config: Config = {
  ...environmentConfig,
  delayIdleTimer: 15, // minutes
  sentry: {
    dsn: 'https://22da4ee306672b3a39be4addfd900343@o379298.ingest.us.sentry.io/4508556569542656',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: environmentConfig.env === 'P' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: environmentConfig.env,
    sendDefaultPii: true,
  },
}

/**
 * Centralised API endpoints configuration
 * Provides type-safe and consistent endpoint definitions across the application
 */
export const API = {
  user: {
    info: '/userinfo',
  },
  antivirus: {
    reload: '/antivirus/reload',
  },
  trustbase: {
    send: '/trustbase/send',
  },
  referential: {
    funds: '/funds',
    vehicles: '/vehicles',
    // `onboarding.InvestmentVehicle` (CRM referential): NOT `vehicles`, which serves another model.
    investmentVehicles: {
      list: '/investment-vehicles',
      detail: (uuid: string) => `/investment-vehicles/${uuid}`,
    },
    // `onboarding.MasterFund` (CRM referential): NOT `opportunity.MasterFund`.
    masterFunds: {
      list: '/master-funds',
      detail: (uuid: string) => `/master-funds/${uuid}`,
    },
    pockets: {
      list: '/pockets',
      detail: (uuid: string) => `/pockets/${uuid}`,
      // Options of the edit form's selects.
      options: {
        strategyFlags: '/pockets/options/strategy-flags',
      },
    },
    // `core.Account` (CRM / MDM referential): NOT the `AccountProxy` models of other apps.
    accounts: {
      list: '/accounts',
      detail: (uuid: string) => `/accounts/${uuid}`,
    },
    // `core.Contact` (CRM referential).
    contacts: {
      list: '/contacts',
      detail: (uuid: string) => `/contacts/${uuid}`,
      resetImpersonating: '/contacts/reset-impersonating',
      // Options of the list filters.
      options: {
        contactTypes: '/contacts/options/contact-types',
      },
    },
    // `registration.RegistrationInformation` + its magic link: one row is one invitation.
    registrations: {
      list: '/registrations',
      detail: (uuid: string) => `/registrations/${uuid}`,
      invite: '/registrations/invite',
      export: '/registrations/export',
    },
    // `core.IDLTemplate`: the access rights of a contact on a vehicle, synced with the CRM.
    idlTemplates: {
      list: '/idl-templates',
      detail: (uuid: string) => `/idl-templates/${uuid}`,
      linkContact: (uuid: string) => `/idl-templates/${uuid}/link-contact`,
      finalize: (uuid: string) => `/idl-templates/${uuid}/finalize`,
      reject: (uuid: string) => `/idl-templates/${uuid}/reject`,
    },
    pocketGroups: {
      list: '/pocket-groups',
      detail: (uuid: string) => `/pocket-groups/${uuid}`,
      // Options of the edit form's selects.
      options: {
        masterFunds: '/pocket-groups/options/master-funds',
        subscriptionAgreementCores: '/pocket-groups/options/subscription-agreement-cores',
        pockets: '/pocket-groups/options/pockets',
        counterSignatories: '/pocket-groups/options/counter-signatories',
      },
    },
  },
  legalConsultations: {
    list: '/legal-consultations',
    detail: (uuid: string) => `/legal-consultations/${uuid}`,
    saveAsDraft: '/legal-consultations/save-as-draft',
    publish: '/legal-consultations/publish',
    modify: (uuid: string) => `/legal-consultations/${uuid}/modify`,
    delete: (uuid: string) => `/legal-consultations/${uuid}/delete`,
    close: (uuid: string) => `/legal-consultations/${uuid}/close`,
    sendForReview: '/legal-consultations/send-for-review',
    documents: (uuid: string) => `/legal-consultations/${uuid}/documents`,
    votingBallots: (uuid: string) => `/legal-consultations/${uuid}/documents/voting-ballots`,
    downloadVotingBallotTemplate: (uuid: string) =>
      `/legal-consultation-answers/${uuid}/voting-ballot/generate-template`,
    uploadDocument: '/legal-consultations/documents/upload',
    modifyDocument: '/legal-consultations/documents/modify',
    deleteDocument: (uuid: string) => `/legal-consultations/documents/${uuid}`,
    reminders: (uuid: string) => `/legal-consultations/${uuid}/reminders`,
    addReminder: () => `/legal-consultations/reminders/add`,
    modifyReminder: (uuid: string) => `/legal-consultations/reminders/${uuid}/modify`,
    deleteReminder: (uuid: string) => `/legal-consultations/reminders/${uuid}/delete`,
    answers: (uuid: string) => `/legal-consultations/${uuid}/answers`,
    modifyAnswer: (uuid: string) => `/legal-consultation-answers/${uuid}/modify`,
    uploadSupportingDocument: (answerUuid: string) =>
      `/legal-consultation-answers/${answerUuid}/supporting-documents/upload`,
    histories: (uuid: string) => `/legal-consultations/${uuid}/histories`,
    emailTracking: (uuid: string) => `/legal-consultations/${uuid}/email-tracking`,
    sendOpeningNotifications: (uuid: string) =>
      `/legal-consultations/${uuid}/send-opening-notifications`,
  },
  chat: {
    messages: (chatUuid: string) => `/chat/${chatUuid}`,
    addMessage: (chatUuid: string) => `/chat/${chatUuid}/messages`,
    updateMessage: (chatUuid: string, commentUuid: string) =>
      `/chat/${chatUuid}/messages/${commentUuid}`,
    deleteMessage: (chatUuid: string, commentUuid: string) =>
      `/chat/${chatUuid}/messages/${commentUuid}/delete`,
  },
  datarooms: {
    list: '/opportunity-datarooms/masterfunds',
    detail: (uuid: string) => `/opportunity-datarooms/masterfunds/${uuid}`,
    saveAsDraft: (uuid: string) => `/opportunity-datarooms/masterfunds/${uuid}/save-as-draft`,
    publish: (uuid: string) => `/opportunity-datarooms/masterfunds/${uuid}/publish`,
    unpublish: (uuid: string) => `/opportunity-datarooms/masterfunds/${uuid}/unpublish`,
    modify: (uuid: string) => `/opportunity-datarooms/masterfunds/${uuid}/modify`,
    histories: (uuid: string) => `/opportunity-datarooms/masterfunds/${uuid}/histories`,
    updateMasterfundPosition: (uuid: string) =>
      `/opportunity-datarooms/masterfunds/${uuid}/position`,
    investmentOpportunities: (uuid: string) =>
      `/opportunity-datarooms/masterfunds/${uuid}/investment-opportunities`,
    opportunityAccesses: (opportunityUuid: string) =>
      `/opportunity-datarooms/opportunities/${opportunityUuid}/accesses`,
    resendInvite: (opportunityAccessUuid: string) =>
      `/opportunity-datarooms/accesses/${opportunityAccessUuid}/resend-invite`,
    folders: {
      get: (uuid: string) => `/opportunity-datarooms/masterfunds/${uuid}/folders`,
      create: (uuid: string) => `/opportunity-datarooms/masterfunds/${uuid}/folders/create`,
      update: (uuid: string, folderId: string) =>
        `/opportunity-datarooms/masterfunds/${uuid}/folders/${folderId}/update`,
      delete: (uuid: string, folderId: string) =>
        `/opportunity-datarooms/masterfunds/${uuid}/folders/${folderId}/delete`,
    },
    groupAccesses: {
      get: (uuid: string) => `/opportunity-datarooms/masterfunds/${uuid}/group-accesses`,
    },
  },
  dedicatedDataroom: {
    saveAsDraft: '/datarooms/save-as-draft',
  },
  onboardings: {
    list: '/onboardings',
    count: '/onboardings/count',
    sendDocumentsToDepositary: '/onboardings/send-documents-to-depositary',
    counterSignSubscriptionAgreement: 'onboardings/subscription-agreement/counter-sign',
    exportSignatorySignedFile: '/onboardings/export-signatory-documents',
    exportCounterSignatorySignedFile: '/onboardings/export-counter-signatory-documents',
    detail: (uuid: string) => `/onboardings/${uuid}`,
    changeActingOnOwnBehalf: (uuid: string) => `/onboardings/${uuid}/change-acting-on-own-behalf`,
    update: (uuid: string) => `/onboardings/${uuid}`,
    close: (uuid: string) => `/onboardings/${uuid}/close`,
    docs: (uuid: string) => `/onboardings/${uuid}/docs`,
    accesses: (uuid: string) => `/onboardings/${uuid}/accesses`,
    resendEmail: (uuid: string, accessUuid: string) =>
      `/onboardings/${uuid}/accesses/${accessUuid}/resend-email`,
    histories: (uuid: string) => `/onboardings/${uuid}/histories`,
    signatories: {
      list: (uuid: string) => `/onboardings/${uuid}/signatories`,
      rejectionHistories: (uuid: string) => `/onboardings/${uuid}/signatories/rejection-histories`,
      reject: (uuid: string) => `/onboardings/${uuid}/signatories/reject`,
      requestRejection: (uuid: string) => `/onboardings/${uuid}/signatories/request-rejection`,
      validate: (uuid: string) => `/onboardings/${uuid}/signatories/validate`,
      invalidate: (uuid: string) => `/onboardings/${uuid}/signatories/invalidate`,
      cancelRejection: (uuid: string) => `/onboardings/${uuid}/signatories/cancel-rejection`,
    },
    aml: {
      responses: (uuid: string) => `/onboardings/${uuid}/aml/form-responses`,
      reject: (uuid: string) => `/onboardings/${uuid}/aml/reject`,
      requestRejection: (uuid: string) => `/onboardings/${uuid}/aml/request-rejection`,
      validate: (uuid: string) => `/onboardings/${uuid}/aml/validate`,
      invalidate: (uuid: string) => `/onboardings/${uuid}/aml/invalidate`,
      cancelRejection: (uuid: string) => `/onboardings/${uuid}/aml/cancel-rejection`,
      rejectionHistories: (uuid: string) => `/onboardings/${uuid}/aml/rejection-histories`,
      readyToSign: (uuid: string) => `/onboardings/${uuid}/aml/ready-to-sign`,
      resendEmail: (uuid: string) => `/onboardings/${uuid}/aml/ready-to-sign/resend-email`,
      cancelReadyToSign: (uuid: string) => `/onboardings/${uuid}/aml/ready-to-sign/cancel`,
      changeSignatory: (uuid: string) => `/onboardings/${uuid}/aml/change-signatory`,
      cancelBypass: (uuid: string) => `/onboardings/${uuid}/aml/signature/cancel-bypass`,
      bypass: (uuid: string) => `/onboardings/${uuid}/aml/signature/bypass`,
      cancelSignatory: (uuid: string) => `/onboardings/${uuid}/aml/signature/cancel`,
      signatureCancellationHistories: (uuid: string) =>
        `/onboardings/${uuid}/aml/signature/cancellation-histories`,
      resendSignatureEmail: (uuid: string) => `/onboardings/${uuid}/aml/signature/resend-email`,
    },
    amlAdditionalDocuments: {
      list: (uuid: string) => `/onboardings/${uuid}/aml-additional-documents`,
      add: (uuid: string) => `/onboardings/${uuid}/aml-additional-documents/request`,
      validateDoc: (uuid: string, documentUuid: string) =>
        `/onboardings/${uuid}/aml-additional-documents/${documentUuid}/validate`,
      invalidateDoc: (uuid: string, documentUuid: string) =>
        `/onboardings/${uuid}/aml-additional-documents/${documentUuid}/invalidate`,
      rejectDoc: (uuid: string, documentUuid: string) =>
        `/onboardings/${uuid}/aml-additional-documents/${documentUuid}/reject`,
      removeDoc: (uuid: string, documentUuid: string) =>
        `/onboardings/${uuid}/aml-additional-documents/${documentUuid}/remove`,
      rejectionHistoriesDoc: (uuid: string, documentUuid: string) =>
        `/onboardings/${uuid}/aml-additional-documents/${documentUuid}/rejection-histories`,
      editDoc: (uuid: string, documentUuid: string) =>
        `/onboardings/${uuid}/aml-additional-documents/${documentUuid}`,
      types: (uuid: string) => `/onboardings/${uuid}/aml-additional-documents/types`,
      counterParties: (uuid: string) =>
        `/onboardings/${uuid}/aml-additional-documents/counter-parties`,

      reject: (uuid: string) => `/onboardings/${uuid}/aml-additional-documents/reject`,
      publish: (uuid: string) => `/onboardings/${uuid}/aml-additional-documents/publish`,
      requestPublication: (uuid: string) =>
        `/onboardings/${uuid}/aml-additional-documents/request-publication`,
      requestRejection: (uuid: string) =>
        `/onboardings/${uuid}/aml-additional-documents/request-rejection`,

      validate: (uuid: string) => `/onboardings/${uuid}/aml-additional-documents/validate`,
      invalidate: (uuid: string) => `/onboardings/${uuid}/aml-additional-documents/invalidate`,
    },
    fatca: {
      responses: (uuid: string) => `/onboardings/${uuid}/fatca/form-responses`,
      reject: (uuid: string) => `/onboardings/${uuid}/fatca/reject`,
      validate: (uuid: string) => `/onboardings/${uuid}/fatca/validate`,
      invalidate: (uuid: string) => `/onboardings/${uuid}/fatca/invalidate`,
      rejectionHistories: (uuid: string) => `/onboardings/${uuid}/fatca/rejection-histories`,
      bypass: (uuid: string) => `/onboardings/${uuid}/fatca/bypass`,
      cancelBypass: (uuid: string) => `/onboardings/${uuid}/fatca/cancel-bypass`,
    },
    subscriptionAgreement: {
      responses: (uuid: string) => `/onboardings/${uuid}/subscription-agreement/form-responses`,
      reject: (uuid: string) => `/onboardings/${uuid}/subscription-agreement/reject`,
      validate: (uuid: string) => `/onboardings/${uuid}/subscription-agreement/validate`,
      invalidate: (uuid: string) => `/onboardings/${uuid}/subscription-agreement/invalidate`,
      rejectionHistories: (uuid: string) =>
        `/onboardings/${uuid}/subscription-agreement/rejection-histories`,
      bypass: (uuid: string) => `/onboardings/${uuid}/subscription-agreement/signature/bypass`,
      cancelBypass: (uuid: string) =>
        `/onboardings/${uuid}/subscription-agreement/signature/cancel-bypass`,
      readyToSign: (uuid: string) => `/onboardings/${uuid}/subscription-agreement/ready-to-sign`,
      resendEmail: (uuid: string) =>
        `/onboardings/${uuid}/subscription-agreement/ready-to-sign/resend-email`,
      cancelReadyToSign: (uuid: string) =>
        `/onboardings/${uuid}/subscription-agreement/ready-to-sign/cancel`,
      cancelSignatory: (uuid: string) =>
        `/onboardings/${uuid}/subscription-agreement/signature/cancel`,
      generateFinalDocument: (uuid: string) =>
        `/onboardings/${uuid}/subscription-agreement/final-document/generate`,
      cancelGenerateFinalDocument: (uuid: string) =>
        `/onboardings/${uuid}/subscription-agreement/final-document/cancel-generation`,
      signatureCancellationHistories: (uuid: string) =>
        `/onboardings/${uuid}/subscription-agreement/signature/cancellation-histories`,
      resendSignatureEmail: (uuid: string) =>
        `/onboardings/${uuid}/subscription-agreement/signature/resend-email`,
    },
    otherDocuments: {
      list: (uuid: string) => `/onboardings/${uuid}/other-documents`,
      reject: (uuid: string, documentUuid: string) =>
        `/onboardings/${uuid}/other-documents/${documentUuid}/reject`,
      validate: (uuid: string, documentType: string) =>
        `/onboardings/${uuid}/other-documents/${documentType}/validate`,
      invalidate: (uuid: string, documentType: string) =>
        `/onboardings/${uuid}/other-documents/${documentType}/invalidate`,
      rejectionHistories: (uuid: string, documentType: string) =>
        `/onboardings/${uuid}/other-documents/${documentType}/rejection-histories`,
      requestRejection: (uuid: string, documentType: string) =>
        `/onboardings/${uuid}/other-documents/${documentType}/request-rejection`,
      cancelRejection: (uuid: string, documentType: string) =>
        `/onboardings/${uuid}/other-documents/${documentType}/cancel-rejection`,
    },
  },
  transfers: {
    list: '/transfers',
    count: '/transfers/count',
    sendDocumentsToDepositary: '/transfers/send-documents-to-depositary',
    detail: (uuid: string) => `/transfers/${uuid}`,
    changeActingOnOwnBehalf: (uuid: string) => `/transfers/${uuid}/change-acting-on-own-behalf`,
    close: (uuid: string) => `/transfers/${uuid}/close`,
    docs: (uuid: string) => `/transfers/${uuid}/docs`,
    accesses: (uuid: string) => `/transfers/${uuid}/accesses`,
    resendEmail: (uuid: string, accessUuid: string) =>
      `/transfers/${uuid}/accesses/${accessUuid}/resend-email`,
    histories: (uuid: string) => `/transfers/${uuid}/histories`,
    signatories: {
      list: (uuid: string) => `/transfers/${uuid}/signatories`,
      rejectionHistories: (uuid: string) => `/transfers/${uuid}/signatories/rejection-histories`,
      reject: (uuid: string) => `/transfers/${uuid}/signatories/reject`,
      requestRejection: (uuid: string) => `/transfers/${uuid}/signatories/request-rejection`,
      validate: (uuid: string) => `/transfers/${uuid}/signatories/validate`,
      invalidate: (uuid: string) => `/transfers/${uuid}/signatories/invalidate`,
      cancelRejection: (uuid: string) => `/transfers/${uuid}/signatories/cancel-rejection`,
    },
    aml: {
      responses: (uuid: string) => `/transfers/${uuid}/aml/form-responses`,
      reject: (uuid: string) => `/transfers/${uuid}/aml/reject`,
      requestRejection: (uuid: string) => `/transfers/${uuid}/aml/request-rejection`,
      validate: (uuid: string) => `/transfers/${uuid}/aml/validate`,
      invalidate: (uuid: string) => `/transfers/${uuid}/aml/invalidate`,
      cancelRejection: (uuid: string) => `/transfers/${uuid}/aml/cancel-rejection`,
      rejectionHistories: (uuid: string) => `/transfers/${uuid}/aml/rejection-histories`,
      readyToSign: (uuid: string) => `/transfers/${uuid}/aml/ready-to-sign`,
      resendEmail: (uuid: string) => `/transfers/${uuid}/aml/ready-to-sign/resend-email`,
      cancelReadyToSign: (uuid: string) => `/transfers/${uuid}/aml/ready-to-sign/cancel`,
      changeSignatory: (uuid: string) => `/transfers/${uuid}/aml/change-signatory`,
      cancelBypass: (uuid: string) => `/transfers/${uuid}/aml/signature/cancel-bypass`,
      bypass: (uuid: string) => `/transfers/${uuid}/aml/signature/bypass`,
      cancelSignatory: (uuid: string) => `/transfers/${uuid}/aml/signature/cancel`,
      signatureCancellationHistories: (uuid: string) =>
        `/transfers/${uuid}/aml/signature/cancellation-histories`,
      resendSignatureEmail: (uuid: string) => `/transfers/${uuid}/aml/signature/resend-email`,
    },
    amlAdditionalDocuments: {
      list: (uuid: string) => `/transfers/${uuid}/aml-additional-documents`,
      add: (uuid: string) => `/transfers/${uuid}/aml-additional-documents/request`,
      validateDoc: (uuid: string, documentUuid: string) =>
        `/transfers/${uuid}/aml-additional-documents/${documentUuid}/validate`,
      invalidateDoc: (uuid: string, documentUuid: string) =>
        `/transfers/${uuid}/aml-additional-documents/${documentUuid}/invalidate`,
      rejectDoc: (uuid: string, documentUuid: string) =>
        `/transfers/${uuid}/aml-additional-documents/${documentUuid}/reject`,
      removeDoc: (uuid: string, documentUuid: string) =>
        `/transfers/${uuid}/aml-additional-documents/${documentUuid}/remove`,
      rejectionHistoriesDoc: (uuid: string, documentUuid: string) =>
        `/transfers/${uuid}/aml-additional-documents/${documentUuid}/rejection-histories`,
      editDoc: (uuid: string, documentUuid: string) =>
        `/transfers/${uuid}/aml-additional-documents/${documentUuid}`,
      types: (uuid: string) => `/transfers/${uuid}/aml-additional-documents/types`,
      counterParties: (uuid: string) =>
        `/transfers/${uuid}/aml-additional-documents/counter-parties`,

      reject: (uuid: string) => `/transfers/${uuid}/aml-additional-documents/reject`,
      publish: (uuid: string) => `/transfers/${uuid}/aml-additional-documents/publish`,
      requestPublication: (uuid: string) =>
        `/transfers/${uuid}/aml-additional-documents/request-publication`,
      requestRejection: (uuid: string) =>
        `/transfers/${uuid}/aml-additional-documents/request-rejection`,

      validate: (uuid: string) => `/transfers/${uuid}/aml-additional-documents/validate`,
      invalidate: (uuid: string) => `/transfers/${uuid}/aml-additional-documents/invalidate`,
    },
    fatca: {
      responses: (uuid: string) => `/transfers/${uuid}/fatca/form-responses`,
      reject: (uuid: string) => `/transfers/${uuid}/fatca/reject`,
      validate: (uuid: string) => `/transfers/${uuid}/fatca/validate`,
      invalidate: (uuid: string) => `/transfers/${uuid}/fatca/invalidate`,
      rejectionHistories: (uuid: string) => `/transfers/${uuid}/fatca/rejection-histories`,
      bypass: (uuid: string) => `/transfers/${uuid}/fatca/bypass`,
      cancelBypass: (uuid: string) => `/transfers/${uuid}/fatca/cancel-bypass`,
    },
    transferAgreement: {
      upload: (uuid: string) => `/transfers/${uuid}/upload-transfer-agreement`,
    },
    otherDocuments: {
      list: (uuid: string) => `/transfers/${uuid}/other-documents`,
      reject: (uuid: string, documentUuid: string) =>
        `/transfers/${uuid}/other-documents/${documentUuid}/reject`,
      validate: (uuid: string, documentType: string) =>
        `/transfers/${uuid}/other-documents/${documentType}/validate`,
      invalidate: (uuid: string, documentType: string) =>
        `/transfers/${uuid}/other-documents/${documentType}/invalidate`,
      rejectionHistories: (uuid: string, documentType: string) =>
        `/transfers/${uuid}/other-documents/${documentType}/rejection-histories`,
      requestRejection: (uuid: string, documentType: string) =>
        `/transfers/${uuid}/other-documents/${documentType}/request-rejection`,
      cancelRejection: (uuid: string, documentType: string) =>
        `/transfers/${uuid}/other-documents/${documentType}/cancel-rejection`,
    },
  },
  videoReplays: {
    list: '/video-replays',
    create: '/video-replays/create',
  },
  subscriptionAgreementGeneration: {
    pockets: '/subscription-agreement-generation/pockets',
    pocket: (uuid: string) => `/subscription-agreement-generation/pockets/${uuid}`,
    investorQuestionnaire: (uuid: string) =>
      `/subscription-agreement-generation/pockets/${uuid}/investor-questionnaire`,
    appendix: (uuid: string) => `/subscription-agreement-generation/pockets/${uuid}/appendixes`,
    appendixesPreview: (uuid: string) =>
      `/subscription-agreement-generation/pockets/${uuid}/appendixes/preview`,
    appendixesDownload: (uuid: string) =>
      `/subscription-agreement-generation/pockets/${uuid}/appendixes/download`,
    appendixDocument: (uuid: string, code: string) =>
      `/subscription-agreement-generation/pockets/${uuid}/appendixes/${encodeURIComponent(code)}/download`,
    appendixPreview: (uuid: string, code: string) =>
      `/subscription-agreement-generation/pockets/${uuid}/appendixes/${encodeURIComponent(code)}/preview`,
  },
  performanceAnalysis: {
    forecastExports: '/forecast/exports',
    forecastExportFile: (uuid: string) => `/forecast/exports/${uuid}/file`,
    // `performance_analysis.SimulationModel`: performance simulations run by the contacts.
    simulations: {
      list: '/simulations',
      detail: (uuid: string) => `/simulations/${uuid}`,
      export: (uuid: string) => `/simulations/${uuid}/export`,
    },
  },
  translations: {
    list: '/translations',
  },
  // FOR STORYBOOK DEMO PURPOSES ONLY
  projects: {
    list: '/projects',
    detail: (id: string) => `/projects/${id}`,
    validate: '/projects/validate',
    reject: '/projects/reject',
    documents: (id: string) => `/projects/${id}/documents`,
    updateStakeholders: (id: string) => `/projects/${id}/stakeholders`,
    updateOverview: (id: string) => `/projects/${id}/overview`,
    milestones: {
      list: (id: string) => `/projects/${id}/milestones`,
      validate: (milestoneId: string) => `/projects/milestones/${milestoneId}`,
      reject: (milestoneId: string) => `/projects/milestones/${milestoneId}`,
    },
  },
  followUps: {
    list: '/follow-ups',
  },
}
